import { AnalyticsCard } from '@/components/analytics/AnalyticsCard'
import { ChartBadge } from '@/components/analytics/ChartBadge'
import { StackedBarChart } from '@/components/analytics/StackedBarChart.tsx'
import { STORY_CYCLE_TIME_CONFIG } from '@/components/analytics/StackedBarChartConfig.tsx'
import { Button } from '@/components/ui/button'
import { STORY_TYPE_CHART_COLORS } from '@/constants/ANALYTICS'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES'
import { STORY_TYPES } from '@/constants/STORY_TYPES'
import { useUserContext } from '@/contexts/UserContext'
import { useProjectId } from '@/hooks/useProjectId'
import { useStackedBarChartData } from '@/hooks/useStackedBarChartData.ts'
import { useQueryAnalyticsIterations } from '@/requests/queries/useQueryAnalytics'
import { toReadableDurationTime } from '@/utils/date.ts'
import { calculateMedianMillis } from '@/utils/math'
import { useNavigate } from 'react-router-dom'

export const STORY_TYPE_COLORS = STORY_TYPE_CHART_COLORS

export const formatHoursWithUnit = (hours: number): string => {
  const unit = hours === 1 ? 'hr' : 'hrs'
  return `${hours} ${unit}`
}

export const StoryCycleTimeOverview = () => {
  const navigate = useNavigate()
  const projectId = useProjectId()
  const { apiKey } = useUserContext()
  const { iterations } = useQueryAnalyticsIterations(apiKey)
  const { storyCycleTimeData } = useStackedBarChartData(iterations)

  const currentIteration = iterations?.[iterations.length - 1]
  if (!currentIteration) return null

  const acceptedStories = currentIteration.stories.filter((s) => s.status === STORY_STATUSES.ACCEPTED)

  const featureCycleTimes = acceptedStories
    .filter((s) => s.storyType === STORY_TYPES.FEATURE)
    .map((s) => s.cycleTimeDetails.totalCycleTime)

  const designCycleTimes = acceptedStories
    .filter((s) => s.storyType === STORY_TYPES.DESIGN)
    .map((s) => s.cycleTimeDetails.totalCycleTime)

  const choreCycleTimes = acceptedStories
    .filter((s) => s.storyType === STORY_TYPES.CHORE)
    .map((s) => s.cycleTimeDetails.totalCycleTime)

  const bugCycleTimes = acceptedStories
    .filter((s) => s.storyType === STORY_TYPES.BUG)
    .map((s) => s.cycleTimeDetails.totalCycleTime)

  const displayMedianTime = (storyType: StoryType, cycleTimes: number[]): string => {
    const millis = calculateMedianMillis(cycleTimes)
    return `${storyType} ${toReadableDurationTime(millis)}`
  }

  return (
    <AnalyticsCard
      title="Story Cycle Time"
      subtitle="Typical time between Start and Accept"
      contents={
        <>
          <Button
            type="button"
            variant="text"
            size="min"
            className="absolute top-[23px] right-[24px]"
            onClick={() => {
              navigate(`/project/${projectId}/analytics/cycle-times`)
            }}
          >
            view report
          </Button>

          <div className="flex flex-col md:flex-row md:justify-between md:items-middle">
            <div className="flex flex-col gap-1 justify-center">
              <ChartBadge variant="Feature" value={displayMedianTime('Feature', featureCycleTimes)} />
              <ChartBadge variant="Design" value={displayMedianTime('Design', designCycleTimes)} />
              <ChartBadge variant="Chore" value={displayMedianTime('Chore', choreCycleTimes)} />
              <ChartBadge variant="Bug" value={displayMedianTime('Bug', bugCycleTimes)} />
            </div>

            <div className="flex-1 h-[180px]" aria-label="Storiy cycle time chart">
              <StackedBarChart data={storyCycleTimeData} config={STORY_CYCLE_TIME_CONFIG} height={180} />
            </div>
          </div>
        </>
      }
    />
  )
}
